import { Opportunity } from "@entities";
import { HailRounded } from "@mui/icons-material";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import { CircularProgress, Dialog, Stack } from "@mui/material";
import { useCreateHyperTable } from "@shared/frontend/hyper-table";
import { isEntityFilter } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/FilterMenuItem";
import { Roles } from "@shared/Roles";
import { type FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useRequest } from "../../../contexts/RequestProvider";
import { remult } from "remult";
import { useUser } from "../../../contexts/UserProvider";
import useQuestion from "../../../dialog-components/QuestionDialog";
import { OpportunityHandlingControls } from "../../../components/opportunity-handling/OpportunityHandlingControls";
import { useCloseQuestion } from "../../../generic-functions/close-question.function";
import AddAidRequestFormDialog from "../../dialogs/AddAidRequestFormDialog";
import OrganizationRequest from "../../organization-request/OrganizationRequest";
import { TableHeaderTitles } from "../table-header/TableHeader";
import { useAssociationRequestedColumns } from "./columns";
import { LATEST_COMMUNICATION_RECORDS_INCLUDE } from "../../../helpers/admin-list-query";
import getFilters from "./filters";
import { CommunicationRecordsDialog } from "../../../components/CommunicationRecordManager/CommunicationRecordsDialog";
import { useCommunicationRecordsDialog } from "../../../components/CommunicationRecordManager/useCommunicationRecordsDialog";

const AssociationRequestedTable: FC = () => {
  const { HyperTable } = useCreateHyperTable(Opportunity);
  const { currOrganization } = useOrganization();
  const { currentUser } = useUser();
  const [request, setRequest] = useState<Opportunity | null>(null);
  const [isAddFormDialogOpen, setIsAddFormDialogOpen] = useState(false);
  const { openCommunicationRecords, communicationRecordsDialogProps } =
    useCommunicationRecordsDialog({
      hyperTable: HyperTable,
      defaultTitle: "בקשת סיוע",
    });
  const navigate = useNavigate();
  const { resetRequest, isTouched } = useRequest();
  const question = useQuestion();
  const closeQuestion = useCloseQuestion();

  useEffect(() => {
    if (!request) {
      HyperTable.context?.current?.refetch("remove");
    }
  }, [request]);

  if (!currOrganization)
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );

  return (
    <HyperTable
      tableId="association-requested-table"
      findConfig={{
        where: Opportunity.hyperFilter({
          isDeleted: false,
          $and: [
            {
              $or: [
                {
                  form: {
                    $is: {
                      team: remult.isAllowed(Roles.Admins)
                        ? {}
                        : {
                          $is: {
                            id: currentUser?.teams?.map((team) => team.teamId),
                          },
                        },
                    },
                  },
                },
                ...(remult.isAllowed(Roles.Admins) ? [{ form: { $is: null } }] : []),
              ],
            },
            { status: "AID_REQUEST" },
            { parentOpportunityId: null },
          ],
        }),
        include: {
          associationRequested: { include: { user: true } },
          handlingManager: true,
          partnerOrganization: true,
          teams: { include: { team: true } },
          categories: { include: { category: true } },
          childOpportunities: true,
          ...LATEST_COMMUNICATION_RECORDS_INCLUDE,
        },
        initialOrderBy: { createdAt: "asc" },
      }}
      title={<TableHeaderTitles titles={[{ content: "בקשות סיוע" }]} />}
      onRowClick={async (_, row) => {
        const opp = row.original;
        if (
          opp.handlingManagerId &&
          opp.handlingManagerId !== currentUser?.id
        ) {
          const managerName = opp.handlingManager?.name ?? "מנהל אחר";
          const res = await question(
            `בקשה זו בטיפול של ${managerName}. להמשיך בכל זאת?`,
            "",
            { confirmText: "המשך", cancelText: "ביטול" },
          );
          if (!res) return;
        }
        navigate("/events/new", {
          state: opp,
        });
      }}
      components={{
        addRowButton: {
          onClick: () => setIsAddFormDialogOpen(true),
          content: "הוספת טופס",
        },
      }}
    >
      <HyperTable.Toolbar
        moreActions={[
          {
            label: "טפסים",
            icon: <DescriptionRoundedIcon fontSize="small" />,
            onClick: () => navigate("/forms"),
          },
          {
            label: "תצוגת מבקשי סיוע",
            icon: <HailRounded fontSize="small" />,
            onClick: () => navigate("/aid-related"),
          },
        ]}
      >
        {getFilters(currOrganization).map((filter) =>
          isEntityFilter(filter) ? (
            <HyperTable.EntityFilter key={filter.id} {...(filter as any)} />
          ) : (
            <HyperTable.CustomFilter key={filter.id} {...filter} />
          ),
        )}
      </HyperTable.Toolbar>
      {useAssociationRequestedColumns(openCommunicationRecords).map((column) => (
        <HyperTable.Column key={column.field} {...column} />
      ))}
      <Dialog
        open={!!request}
        PaperProps={{
          sx: {
            width: "40vw",
            height: "90vh",
            maxWidth: "90vw",
            margin: 0,
            overflow: "hidden",
          },
        }}
        onClose={() => {
          if (isTouched) {
            closeQuestion({
              onYes: () => {
                setRequest(null);
                resetRequest();
              },
            });
          } else {
            setRequest(null);
            resetRequest();
          }
        }}
      >
        <OrganizationRequest
          setIsAddEvent={() => setRequest(null)}
          eventData={request || undefined}
          fullPage={false}
        />
      </Dialog>
      <AddAidRequestFormDialog
        open={isAddFormDialogOpen}
        onClose={() => setIsAddFormDialogOpen(false)}
      />
      {communicationRecordsDialogProps.open && (
        <CommunicationRecordsDialog {...communicationRecordsDialogProps} />
      )}
    </HyperTable>
  );
};

export default AssociationRequestedTable;
